import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import './FinanceDashboard.css';
import ImagesPaymentsTable from './ImagesPaymentsTable';

const FinanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [debtModal, setDebtModal] = useState(null); // 'course' or '083'
  const navigate = useNavigate();

  const adminLocation = localStorage.getItem('adminLocation') || '';
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'superadmin';

  useEffect(() => {
    const fetchFinanceData = async () => {
      setLoading(true);
      try {
        let location = adminLocation;
        
        // Fetch location if missing
        if (!location && !isSuperAdmin) {
          const currentUserEmail = localStorage.getItem('userEmail') || '';
          if (currentUserEmail) {
            const adminsSnapshot = await getDocs(collection(db, 'newadmins'));
            adminsSnapshot.forEach((d) => {
              const adminData = d.data();
              if (adminData.email === currentUserEmail.toLowerCase().trim()) {
                location = adminData.location || '';
                if (location) localStorage.setItem('adminLocation', location);
              }
            });
          }
        }

        let q;
        if (isSuperAdmin) {
          q = query(collection(db, "images"));
        } else {
          if (!location) {
            setError("Lokatsiya topilmadi.");
            setLoading(false);
            return;
          }
          q = query(collection(db, "images"), where("location", "==", location.toString().trim()));
        }

        const snap = await getDocs(q);
        const list = [];
        snap.forEach((d) => {
          list.push({ id: d.id, ...d.data() });
        });

        setUsers(list);
      } catch (err) {
        console.error("Error fetching finance data:", err);
        setError("Ma'lumotlarni yuklashda xatolik yuz berdi.");
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceData();
  }, [adminLocation, isSuperAdmin]);

  const { totalIncome, total083Income, outstandingCourseDebt, outstanding083Debt, fullyPaidCount, locationStats, conversionStats, conversionRate, courseDebtors, forma083Debtors } = useMemo(() => {
    let tIncome = 0;
    let t083Income = 0;
    let oCourseDebt = 0;
    let o083Debt = 0;
    let fPaidCount = 0;
    const lStats = {}; // Location stats
    let courseBuyers = 0;
    let forma083Buyers = 0;
    const cDebtors = [];
    const f083Debtors = [];

    users.forEach(u => {
      // Course calcs
      const courseAgreed = Number(u.agreedAmount) || 0;
      const coursePaid = (u.debtPayments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      const currentCDebt = Math.max(0, courseAgreed - coursePaid);
      tIncome += coursePaid;
      oCourseDebt += currentCDebt;
      if (currentCDebt > 0) cDebtors.push({ ...u, debtAmount: currentCDebt });
      
      if (courseAgreed > 0) courseBuyers++;

      // 083 calcs
      const f083Agreed = u.agreedAmountForma083 === null || u.agreedAmountForma083 === undefined ? null : Number(u.agreedAmountForma083);
      const f083Paid = (u.forma083Payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      t083Income += f083Paid;
      if (f083Agreed !== null) {
        const currentFDebt = Math.max(0, f083Agreed - f083Paid);
        o083Debt += currentFDebt;
        if (currentFDebt > 0) f083Debtors.push({ ...u, debtAmount: currentFDebt });
      }
      
      if (f083Agreed !== null && f083Agreed > 0) forma083Buyers++;

      // Check fully paid
      if (
        (courseAgreed > 0 && Math.max(0, courseAgreed - coursePaid) === 0) &&
        (f083Agreed === null || Math.max(0, f083Agreed - f083Paid) === 0)
      ) {
        fPaidCount++;
      }

      // Location Stats Calculation
      const loc = u.location || 'Noma\'lum';
      if (!lStats[loc]) {
        lStats[loc] = { name: loc, income: 0, forma083Income: 0 };
      }
      lStats[loc].income += coursePaid;
      lStats[loc].forma083Income += f083Paid;
    });

    // Sort debtors by debt amount descending
    cDebtors.sort((a, b) => b.debtAmount - a.debtAmount);
    f083Debtors.sort((a, b) => b.debtAmount - a.debtAmount);

    // Convert to sorted array
    const sortedLocStats = Object.values(lStats).sort((a, b) => b.income - a.income);
    
    // Fallback if no specific course buyers detected (edge case)
    if (courseBuyers === 0 && users.length > 0) courseBuyers = users.length;
    
    const convStats = [
      { name: '083 Xarid qildi', value: forma083Buyers, fill: '#10b981' },
      { name: 'Faqat Asosiy Kurs', value: Math.max(0, courseBuyers - forma083Buyers), fill: '#e2e8f0' }
    ];
    const convRate = courseBuyers > 0 ? Math.round((forma083Buyers / courseBuyers) * 100) : 0;

    return { totalIncome: tIncome, total083Income: t083Income, outstandingCourseDebt: oCourseDebt, outstanding083Debt: o083Debt, fullyPaidCount: fPaidCount, locationStats: sortedLocStats, conversionStats: convStats, conversionRate: convRate, courseDebtors: cDebtors, forma083Debtors: f083Debtors };
  }, [users]);

  // Chart Data Preparation
  const chartData = useMemo(() => {
    const monthlyData = {};
    
    users.forEach(user => {
      // Process course payments
      (user.debtPayments || []).forEach(p => {
        if (!p.timestamp) return;
        const d = new Date(p.timestamp);
        const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyData[month]) monthlyData[month] = { name: month, Asosiy: 0, 'Forma 083': 0 };
        monthlyData[month].Asosiy += (Number(p.amount) || 0);
      });
      
      // Process 083 payments
      (user.forma083Payments || []).forEach(p => {
        if (!p.timestamp) return;
        const d = new Date(p.timestamp);
        const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyData[month]) monthlyData[month] = { name: month, Asosiy: 0, 'Forma 083': 0 };
        monthlyData[month]['Forma 083'] += (Number(p.amount) || 0);
      });
    });

    return Object.values(monthlyData).sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const formatUZS = (val) => {
    if (!val) return "0 UZS";
    return val.toLocaleString("ru-RU") + " UZS";
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <div>Moliya ma'lumotlari yuklanmoqda...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div style={{ color: 'red', textAlign: 'center', marginTop: '50px' }}>{error}</div>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('userRole');
    localStorage.removeItem('adminLocation');
    navigate('/login');
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Moliya Dashboard</h1>
          <p className="dashboard-subtitle">
            {isSuperAdmin ? 'Barcha lokatsiyalar bo\'yicha umumiy hisobot' : `Lokatsiya: ${adminLocation} bo'yicha hisobot`}
          </p>
        </div>
        <button className="back-btn" onClick={() => navigate('/')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Jadvalga Qaytish
        </button>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card blue">
          <div className="kpi-title">Umumiy Daromad (Asosiy Kurs)</div>
          <h2 className="kpi-value">{formatUZS(totalIncome)}</h2>
          <p className="kpi-desc">Barcha to'langan asosiy to'lovlar yig'indisi</p>
        </div>
        
        <div className="kpi-card purple">
          <div className="kpi-title">083 Forma Daromadi</div>
          <h2 className="kpi-value">{formatUZS(total083Income)}</h2>
          <p className="kpi-desc">Barcha 083 forma to'lovlari yig'indisi</p>
        </div>
        
        <div className="kpi-card red" style={{ cursor: 'pointer' }} onClick={() => setDebtModal('course')}>
          <div className="kpi-title">Asosiy Kurs Qarzi <span style={{fontSize: '12px', background: '#fee2e2', color: '#ef4444', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px'}}>Ro'yxat 👁</span></div>
          <h2 className="kpi-value">{formatUZS(outstandingCourseDebt)}</h2>
          <p className="kpi-desc">To'lanmagan asosiy kurs to'lovlari (Bosing)</p>
        </div>

        <div className="kpi-card red" style={{ borderColor: '#f87171', cursor: 'pointer' }} onClick={() => setDebtModal('083')}>
          <div className="kpi-title">083 Forma Qarzi <span style={{fontSize: '12px', background: '#fee2e2', color: '#ef4444', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px'}}>Ro'yxat 👁</span></div>
          <h2 className="kpi-value">{formatUZS(outstanding083Debt)}</h2>
          <p className="kpi-desc">To'lanmagan 083 forma to'lovlari (Bosing)</p>
        </div>
        
        <div className="kpi-card green">
          <div className="kpi-title">To'liq to'lagan o'quvchilar</div>
          <h2 className="kpi-value">{fullyPaidCount} <span style={{fontSize: '18px', color: '#64748b', fontWeight: '500'}}> / {users.length}</span></h2>
          <p className="kpi-desc">Barcha qarzlari uzilgan foydalanuvchilar soni</p>
        </div>
      </div>

      {isSuperAdmin && locationStats && locationStats.length > 0 && (
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>Lokatsiyalar bo'yicha Daromadlar</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {locationStats.map(stat => (
              <div key={stat.name} style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', transition: 'transform 0.2s', cursor: 'default' }} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>📍</span> {stat.name}
                </h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>Asosiy Kurs:</span>
                  <strong style={{ color: '#3b82f6' }}>{formatUZS(stat.income)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>083 Forma:</span>
                  <strong style={{ color: '#8b5cf6' }}>{formatUZS(stat.forma083Income)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#334155', fontSize: '15px', fontWeight: 'bold' }}>Jami Tushum:</span>
                  <strong style={{ color: '#10b981', fontSize: '16px' }}>{formatUZS(stat.income + stat.forma083Income)}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="charts-section">
          <div className="chart-card">
            <h3 className="chart-title">Oylik Daromadlar (Bar Chart)</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value / 1000}k`} />
                  <Tooltip 
                    cursor={{fill: 'rgba(241, 245, 249, 0.5)'}}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                    formatter={(value) => formatUZS(value)}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="Asosiy" stackId="a" fill="#3b82f6" radius={[0, 0, 4, 4]} />
                  <Bar dataKey="Forma 083" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-card">
            <h3 className="chart-title">Oylik O'sish Dinamikasi (Line Chart)</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value / 1000}k`} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                    formatter={(value) => formatUZS(value)}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line type="monotone" dataKey="Asosiy" stroke="#3b82f6" strokeWidth={3} activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="Forma 083" stroke="#8b5cf6" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="chart-card">
            <h3 className="chart-title">083 Forma Xarid Qilish Ko'rsatkichi</h3>
            <div className="chart-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={conversionStats}
                    innerRadius={100}
                    outerRadius={140}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {conversionStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} ta o'quvchi`} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', textAlign: 'center', pointerEvents: 'none', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', marginTop: '-15px' }}>
                <span style={{ fontSize: '42px', fontWeight: 'bold', color: '#0f172a' }}>{conversionRate}%</span>
                <br />
                <span style={{ fontSize: '14px', color: '#64748b' }}>xarid ko'rsatkichi</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embed the detailed table directly below the dashboard charts so everything is in one place */}
      <div style={{ marginTop: '60px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '20px' }}>Batafsil To'lovlar Jadvali</h2>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', backgroundColor: 'white', padding: '20px' }}>
           <ImagesPaymentsTable 
             adminLocation={adminLocation} 
             userRole={userRole}
             onLogout={handleLogout}
             hideHeader={true} // Add this prop conditionally to ImagesPaymentsTable if we don't want duplicate titles
           />
        </div>
      </div>

      {debtModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setDebtModal(null)}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '16px', width: '100%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                {debtModal === 'course' ? "Asosiy Kurs Qarzdorlari" : "083 Forma Qarzdorlari"}
              </h2>
              <button onClick={() => setDebtModal(null)} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', fontSize: '20px', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#e2e8f0'} onMouseOut={e => e.currentTarget.style.background = '#f1f5f9'}>✕</button>
            </div>
            
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 15px', background: '#f8fafc', color: '#475569', fontSize: '14px', fontWeight: '600', borderBottom: '2px solid #e2e8f0' }}>Ism Familiya</th>
                  <th style={{ padding: '12px 15px', background: '#f8fafc', color: '#475569', fontSize: '14px', fontWeight: '600', borderBottom: '2px solid #e2e8f0' }}>Telefon</th>
                  <th style={{ padding: '12px 15px', background: '#f8fafc', color: '#475569', fontSize: '14px', fontWeight: '600', borderBottom: '2px solid #e2e8f0', textAlign: 'right' }}>Qarz Miqdori</th>
                </tr>
              </thead>
              <tbody>
                {(debtModal === 'course' ? courseDebtors : forma083Debtors).map((d, idx) => (
                  <tr key={d.id} style={{ background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={{ padding: '12px 15px', borderBottom: '1px solid #e2e8f0', color: '#1e293b', fontWeight: '500' }}>{d.name || d.surname || 'Noma\'lum'}</td>
                    <td style={{ padding: '12px 15px', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>{d.phone || d.phoneNumber || '-'}</td>
                    <td style={{ padding: '12px 15px', borderBottom: '1px solid #e2e8f0', color: '#ef4444', fontWeight: '700', textAlign: 'right' }}>{formatUZS(d.debtAmount)}</td>
                  </tr>
                ))}
                {(debtModal === 'course' ? courseDebtors : forma083Debtors).length === 0 && (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                      Muvaffaqiyat! Hozirda qarzdorlar yo'q.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceDashboard;
